import { z } from 'zod';

/*
 * The background worker. reference/data-model.md §10, ROADMAP.md §7.6.
 *
 * **The task names are the contract.** They are the `task_name` in the ledger,
 * the key on the dashboard, and what a manual re-run is asked for by — so they
 * are written down once, here, and both sides import them. A task named
 * differently in two places is a task whose history splits in half the day
 * somebody fixes the typo.
 */

/*
 * > **`email-retry` was removed in phase 9A, and this is the decision rather
 * > than an omission.**
 * >
 * > It was registered from 7.6 and never built, because a retry needs the
 * > message body and `email_log` deliberately stores none. 9A changed half of
 * > that: `conversation_message` *does* store bodies, so retrying a failed
 * > conversation reply became possible for the first time. It is still the
 * > wrong thing to build, for three reasons:
 * >
 * >   1. **Most send failures are permanent.** `550 mailbox unavailable` does
 * >      not become deliverable in fifteen minutes, and retrying it four times
 * >      an hour spends reputation on a cold sending domain to achieve nothing.
 * >   2. **The transient ones are already handled by somebody else.** Mail
 * >      leaves through GoDaddy's relay, which queues and retries on its own;
 * >      once it accepts a message, failure comes back later on the bounce lane
 * >      rather than as an error we could retry.
 * >   3. **It could only ever cover conversation replies.** Password resets and
 * >      notifications still store no body, so a task called `email-retry`
 * >      would retry a fraction of the mail and the dashboard would still be
 * >      lying — just less obviously.
 * >
 * > What replaces it is not a task: a failed reply stays on the thread marked
 * > "Not delivered" with the receiving server's own words, and a person decides
 * > whether to send it again. That is better than a machine retrying a permanent
 * > failure, and it is the behaviour 9.4 already ships.
 * >
 * > **Removed rather than left registered**, which is the opposite of what 8.1
 * > did with it, and deliberately: a task left on the dashboard describing work
 * > nobody intends to do is worse than a gap, because it reads as a thing that
 * > is running.
 */

export const TASK_NAMES = [
  'generate-recurring-jobs',
  'job-reminders',
  'stale-clock-in-alert',
  'expired-push-cleanup',
  'database-backup',
  'calendar-sync',
  // 11.2's restraint rule made a task: a busy thread must not email the office
  // per message, so the email half of "customer replied" is this digest.
  'reply-digest',
  // Empties the admin recycle bin — owner decision 2026-08-07, 30 days.
  'purge-deleted-admins',
] as const;

export type TaskName = (typeof TASK_NAMES)[number];

/** Display names. A raw `stale-clock-in-alert` must never reach a screen (P5). */
export const TASK_LABELS: Record<TaskName, string> = {
  'generate-recurring-jobs': 'Generate recurring jobs',
  'job-reminders': 'Job reminders',
  'stale-clock-in-alert': 'Stale clock-in alert',
  'expired-push-cleanup': 'Expired push cleanup',
  'database-backup': 'Database backup',
  'calendar-sync': 'Calendar sync',
  'reply-digest': 'Reply digest',
  'purge-deleted-admins': 'Empty the admin recycle bin',
};

/** What each one is for, in the office's language rather than the cron's. */
export const TASK_DESCRIPTIONS: Record<TaskName, string> = {
  'generate-recurring-jobs': 'Puts standing arrangements on the calendar, fourteen days ahead.',
  'job-reminders': 'Tells the crew about tomorrow’s jobs, a day before.',
  'stale-clock-in-alert':
    'Finds anybody still clocked in after sixteen hours — almost always a forgotten clock-out.',
  'expired-push-cleanup': 'Drops phone subscriptions that no longer accept notifications.',
  'database-backup': 'A nightly copy, kept off the application’s own disk.',
  'calendar-sync': 'Pulls short-term rental bookings in and turns checkouts into turnovers.',
  'reply-digest':
    'Emails the office about customer replies nobody has opened — once an hour, never per message.',
  'purge-deleted-admins':
    'Removes admin accounts deleted more than 30 days ago. Until then they can be restored.',
};

/** When each runs, in words. The schedule itself lives in the worker. */
export const TASK_SCHEDULES: Record<TaskName, string> = {
  'generate-recurring-jobs': 'Nightly, 02:10',
  'job-reminders': 'Nightly, 18:00',
  'stale-clock-in-alert': 'Hourly',
  'expired-push-cleanup': 'Nightly, 03:40',
  'database-backup': 'Nightly, 03:17 — on the server, not in here',
  'calendar-sync': 'Every four hours',
  'reply-digest': 'Hourly, at :15',
  'purge-deleted-admins': 'Nightly, 03:50',
};

/**
 * Tasks with nothing behind them yet, and the phase that builds each.
 *
 * **Registered from the start rather than added later**, so the dashboard lists
 * every task the business will have and says which are not wired up. A dashboard
 * that only shows what happens to exist cannot tell an admin that job reminders
 * are not running — it just does not mention them, which reads as fine.
 */
export const TASK_PENDING_PHASE: Partial<Record<TaskName, string>> = {
  // job-reminders became real in 11.2, expired-push-cleanup in 11.4, and
  // calendar-sync in 11.6 — the phase that emptied this list of phases.
  'database-backup': 'Runs on the server itself — see PROGRESS.md',
};

export const runTaskSchema = z.object({
  taskName: z.enum(TASK_NAMES),
});

export type RunTaskInput = z.infer<typeof runTaskSchema>;

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

export interface TaskRunView {
  id: string;
  taskName: string;
  /** `generate-recurring-jobs:2026-08-04` — the claim. */
  runKey: string;
  status: 'running' | 'ok' | 'error';

  /**
   * Still `running` long after it started — a process that died mid-task.
   *
   * A different thing from "still going" and worth saying, because the run key
   * is still claimed: the task will not run again on its own until the period
   * rolls over.
   */
  isStuck: boolean;

  startedAt: string;
  finishedAt: string | null;
  /** Null while it is still running. */
  durationMs: number | null;
  /** Counts, skips, the error message — whatever the task reported. */
  detail: unknown;
}

export interface TaskSummary {
  taskName: string;
  /** Null when it has never run. The dashboard says so rather than blank. */
  lastRun: TaskRunView | null;
  recentRuns: TaskRunView[];
}
